import { type ReactNode } from 'react';
import { HugeiconsIcon, type IconSvgElement } from '@hugeicons/react';
import { cn } from '@/lib/utils';

interface EmptyStateProps {
  icon: IconSvgElement;
  title: ReactNode;
  hint?: ReactNode;
  action?: ReactNode;
  /** `sm` = compact (charts, inline). `md` = fuller (tables, pages). */
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * Design-system empty state — icon-in-circle + text. Single source for the
 * "no data" pattern. `sm` matches the compact dashboard-chart empties; `md`
 * is the fuller table/page empty state with an optional description + action.
 */
export function EmptyState({ icon, title, hint, action, size = 'md', className }: EmptyStateProps) {
  const sm = size === 'sm';
  return (
    <div className={cn('flex flex-col items-center justify-center text-center', sm ? 'gap-2 py-8' : 'py-12 px-6', className)}>
      <div className={cn('rounded-full bg-gray-100 flex items-center justify-center', sm ? 'w-10 h-10 mb-1' : 'w-11 h-11 mb-3')}>
        <HugeiconsIcon icon={icon} size={sm ? 20 : 22} strokeWidth={sm ? 2 : 1.8} color="currentColor" className={cn('text-gray-400', sm ? 'w-5 h-5' : '')} aria-hidden="true" />
      </div>
      <p className={sm ? 'text-sm text-gray-400' : 'text-sm font-medium text-gray-900'}>{title}</p>
      {hint && <p className="text-xs text-gray-500 mt-1 max-w-xs">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
